<template>
  <Card>
    <template #title>Reports</template>
    <template #content>
      <div class="flex flex-col w-full h-[70vh]">
        <div class="w-full flex-auto">
          <ag-grid-vue
            ref="dataGrid"
            theme="legacy"
            :rowData="reports"
            :columnDefs="colDefs"
            :autoSizeStrategy="{ type: 'fitGridWidth' }"
            :suppressCellFocus="true"
            class="mx-4 h-full"
            :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
            :singleClickEdit="true"
            :stopEditingWhenCellsLoseFocus="true"
            :suppressDragLeaveHidesColumns="true"
            @row-data-updated="resize"
            @grid-size-changed="resize"
            @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
          >
          </ag-grid-vue>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import { ListOfReportDataSchema, type ReportData } from "@/types/Reports";
import CustomCheckboxRenderer from "../cell-renderers/CustomCheckboxRenderer.vue";
import type { GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent } from "ag-grid-community";

const reports = ref<ReportData[]>([]);

const gridApi = ref<GridApi>();

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

onMounted(async (): Promise<void> => {
  axios
    .get("/reports/list/ALL_REPORTS?include_disabled=true")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          reports.value = ListOfReportDataSchema.parse(data).reports;
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
});

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const toggleReport = (index: number, checked: boolean): void => {
  const dataRow = gridApi.value?.getDisplayedRowAtIndex(index);
  if (dataRow == null) return;

  dataRow.setDataValue("enabled", checked);

  const report: ReportData = dataRow.data;

  let data = new FormData();
  data.append("name", report.id);
  data.append("value", report.enabled ? "true" : "false");

  axios
    .post("/settings/update", data)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", report.name + (report.enabled ? " enabled" : " disabled"));
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
          dataRow.setDataValue("enabled", !report.enabled);
        }
      );
    })
    .catch((e) => {
      errorApi(e);
      dataRow.setDataValue("enabled", !report.enabled);
    });
};

const colDefs = ref([
  {
    field: "enabled",
    headerName: "Enabled",
    cellDataType: "boolean",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    width: 25,
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: () => {
      return {
        setValue: toggleReport,
        disabled: false,
      };
    },
  },
  {
    field: "name",
    headerName: "Name",
    width: 100,
  },
  {
    field: "context",
    headerName: "Type",
    width: 50,
  },
  {
    field: "options",
    headerName: "Options",
    width: 100,
  },
]);
</script>
